import { SLUG_TYPE_QUERY_PARAMETER_NAME } from 'config/constants';
// eslint-disable-next-line no-restricted-imports
import NextLink, { LinkProps } from 'next/link';
import { ComponentPropsWithoutRef, MouseEventHandler, ReactElement } from 'react';
import { PageType } from 'store/slices/createPageLoadingStateSlice';
import { useSessionStore } from 'store/useSessionStore';
import {
    FriendlyPagesDestinations,
    FriendlyPagesTypes,
    FriendlyPagesTypesKey,
    FriendlyPagesTypesKeys,
} from 'types/friendlyUrl';

type ExtendedNextLinkProps = Omit<ComponentPropsWithoutRef<'a'>, keyof LinkProps> &
    Omit<LinkProps, 'prefetch'> & {
        type?: PageType;
        queryParams?: Record<string, string>;
        shouldRedirectOnClick?: boolean;
        children?: ReactElement | string | number;
    };

export const ExtendedNextLink: FC<ExtendedNextLinkProps> = ({
    children,
    href,
    queryParams,
    onClick,
    as,
    shouldRedirectOnClick = true,
    type,
    ...props
}) => {
    const { updatePageLoadingState } = useSessionStore((s) => s);
    const isDynamic = type && FriendlyPagesTypesKeys.includes(type as any);

    const handleOnClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
        const mouseWheelClick = e.button === 1;
        const isWithoutOpeningInNewTab = !e.ctrlKey && !e.metaKey && !mouseWheelClick;

        if (isWithoutOpeningInNewTab) {
            onClick?.(e);

            if (!shouldRedirectOnClick) {
                e.preventDefault();

                return;
            }

            if (type) {
                updatePageLoadingState({ isPageLoading: true, redirectPageType: type });
            } else {
                updatePageLoadingState({ redirectPageType: undefined });
            }
        }
    };

    return (
        <NextLink
            as={isDynamic ? href : as}
            prefetch={false}
            tabIndex={0}
            href={
                isDynamic
                    ? {
                          pathname: FriendlyPagesDestinations[type as FriendlyPagesTypesKey],
                          query: {
                              [SLUG_TYPE_QUERY_PARAMETER_NAME]: FriendlyPagesTypes[type as FriendlyPagesTypesKey],
                              ...queryParams,
                          },
                      }
                    : href
            }
            onClick={handleOnClick}
            {...props}
        >
            {children}
        </NextLink>
    );
};
